
import React, { useState, useMemo } from 'react';
import { useStore } from '../hooks/useStore.ts';
import { api } from '../services/api.ts';
import { Plus, Search, Filter, HardHat, ChevronDown, ChevronUp, Sparkles, Globe, Loader2, ExternalLink, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Modal } from '../components/Modal.tsx';
import { useToast } from '../hooks/useToast.ts';


export const ProjectList: React.FC = () => {
  const projects = useStore(() => api.projects.list(), [], 'projects') || [];
  const { addToast } = useToast();

  const [modal, setModal] = useState<boolean>(false);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('All');
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  
  // Market Intel State
  const [intelLoading, setIntelLoading] = useState(false);
  const [marketIntel, setMarketIntel] = useState<{ text: string, sources: any[] } | null>(null);

  const today = new Date().toISOString().split('T')[0];
  const [newP, setNewP] = useState<any>({ 
    title: '', status: 'Active', contractAmount: 0, location: 'Guam',
    startDate: today, endDate: today, client: ''
  });

  const filtered = useMemo(() => projects.filter(p => 
    (p.title.toLowerCase().includes(search.toLowerCase()) || p.client.toLowerCase().includes(search.toLowerCase())) &&
    (filter === 'All' || p.status === filter)
  ), [projects, search, filter]);

  const saveProject = async () => {
    if (newP.title) {
        await api.projects.add({ ...newP, description: '', client: newP.client || 'General Client' });
        setModal(false);
        setNewP({ title: '', status: 'Active', contractAmount: 0, location: 'Guam', startDate: today, endDate: today, client: '' });
        addToast({ type: 'success', title: 'Registry Updated', message: `"${newP.title}" has been registered.` });
    }
  };

  const getMarketIntelligence = async () => {
    setIntelLoading(true);
    try {
      const response = await ai.models.generateContent({
        model: 'gemini-3-flash-preview',
        contents: 'What are the current logistics bottlenecks and construction trends for the Guam and CNMI region in 2024 and 2025? Provide a concise brief for a project manager.',
        config: {
          tools: [{ googleSearch: {} }]
        }
      });
      
      const text = response.text || "No intelligence found.";
      const sources = response.candidates?.[0]?.groundingMetadata?.groundingChunks || [];
      setMarketIntel({ text, sources });
    } catch (e) {
      addToast({ type: 'error', title: 'Search Failed', message: 'Could not reach grounding engine.' });
    } finally {
      setIntelLoading(false);
    }
  };

  const toggleExpand = (id: number) => {
      const next = new Set(expanded);
      if (next.has(id)) next.delete(id); else next.add(id);
      setExpanded(next);
  };

  return (
    <div className="space-y-10 pb-32 max-w-7xl mx-auto animate-in fade-in duration-700">
       <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8">
           <div className="space-y-2">
               <h1 className="text-3xl font-black text-black tracking-tight uppercase">Job Book</h1>
               <p className="text-zinc-500 font-medium text-sm">Active contracts and operational builds.</p>
           </div>
           <div className="flex gap-4">
               <button 
                  onClick={getMarketIntelligence}
                  disabled={intelLoading}
                  className="px-6 py-3 bg-white border border-zinc-200 text-black rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-zinc-50 transition-all shadow-sm flex items-center gap-2"
               >
                   {intelLoading ? <Loader2 size={16} className="animate-spin" /> : <Globe size={16} className="text-zinc-400" />} 
                   <span>Market Intel</span>
               </button>
               <button onClick={() => setModal(true)} className="px-6 py-3 bg-black text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:scale-105 transition-all shadow-lg flex items-center gap-2">
                   <Plus size={16}/> <span>Register Job</span>
               </button>
           </div>
       </div>

       {marketIntel && (
         <div className="bg-zinc-900 text-white rounded-[2.5rem] p-10 shadow-2xl animate-in slide-in-from-top-8 relative overflow-hidden">
            <div className="absolute top-0 right-0 p-8 opacity-5"><Globe size={120} /></div>
            <div className="relative z-10 space-y-6">
                <div className="flex justify-between items-center">
                    <h3 className="text-xl font-black uppercase tracking-tighter flex items-center gap-3">
                        <Sparkles className="text-amber-400" size={24} /> Regional Intelligence Brief
                    </h3>
                    <button onClick={() => setMarketIntel(null)} className="text-zinc-600 hover:text-white transition-colors"><X size={20}/></button>
                </div>
                <div className="text-zinc-400 text-sm font-medium leading-relaxed prose prose-invert max-w-none">
                    {marketIntel.text}
                </div>
                <div className="pt-6 border-t border-zinc-800">
                    <h4 className="text-[10px] font-black uppercase text-zinc-600 tracking-widest mb-4">Grounding Sources</h4>
                    <div className="flex flex-wrap gap-3">
                        {marketIntel.sources.map((src, i) => (
                           src.web?.uri && (
                            <a key={i} href={src.web.uri} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-4 py-2 bg-zinc-800 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-zinc-700 transition-colors">
                                {src.web.title || `Source ${i+1}`} <ExternalLink size={10} />
                            </a>
                           )
                        ))}
                    </div>
                </div>
            </div>
         </div>
       )}

       <div className="bg-white border border-zinc-200 rounded-[2.5rem] overflow-hidden shadow-sm">
           <div className="p-6 border-b border-zinc-100 flex flex-col md:flex-row gap-4 bg-zinc-50/30">
               <div className="relative flex-1">
                   <Search className="absolute left-5 top-1/2 -translate-y-1/2 text-zinc-300" size={16}/>
                   <input className="w-full pl-12 pr-5 py-3.5 rounded-2xl border border-zinc-100 bg-white font-bold text-sm focus:outline-none focus:border-zinc-400 transition-colors" placeholder="Filter jobs or clients..." value={search} onChange={e => setSearch(e.target.value)} />
               </div>
               <div className="relative">
                   <Filter className="absolute left-5 top-1/2 -translate-y-1/2 text-zinc-300" size={16}/>
                   <select className="pl-12 pr-10 py-3.5 rounded-2xl border border-zinc-100 appearance-none bg-white focus:outline-none font-bold text-sm transition-colors min-w-[160px]" value={filter} onChange={e => setFilter(e.target.value)}>
                       {['All', 'Bidding', 'Pre-Con', 'Active', 'Completed'].map(s => <option key={s} value={s}>{s} Phase</option>)}
                   </select>
               </div>
           </div>
           
           <div className="divide-y divide-zinc-50">
               {filtered.map(p => (
                   <div key={p.id} className="group hover:bg-zinc-50/50 transition-colors duration-300">
                       <div className="p-6 flex items-center gap-6 cursor-pointer" onClick={() => toggleExpand(p.id!)}>
                           <div className={`w-12 h-12 rounded-xl flex items-center justify-center font-black shrink-0 border ${p.status==='Active'?'bg-black text-white border-black':'bg-white text-zinc-300 border-zinc-100'}`}>
                               <HardHat size={20}/>
                           </div>
                           <div className="flex-1 min-w-0">
                               <div className="flex justify-between items-center">
                                   <h3 className="text-lg font-black text-black tracking-tight">{p.title}</h3>
                                   <span className="text-[8px] font-black uppercase text-zinc-400 tracking-widest bg-zinc-100/50 px-2 py-1 rounded">{p.status}</span>
                               </div>
                               <p className="text-xs text-zinc-500 font-medium mt-1">{p.client} • <span className="text-black font-black">${(p.contractAmount / 1000).toLocaleString()}k</span></p>
                           </div>
                           <div className="flex items-center gap-4">
                             <Link to={`/projects/${p.id}`} onClick={e => e.stopPropagation()} className="px-4 py-2 text-[9px] font-black uppercase tracking-widest text-white bg-black rounded-lg hover:scale-105 transition-all opacity-0 group-hover:opacity-100">
                               Controls
                             </Link>
                             <button className="text-zinc-200 group-hover:text-black transition-colors">
                               {expanded.has(p.id!) ? <ChevronUp size={20}/> : <ChevronDown size={20}/>}
                             </button>
                           </div>
                       </div>
                   </div>
               ))}
           </div>
       </div>

       <Modal isOpen={modal} onClose={() => setModal(false)} title="Operational Job Registry" maxWidth="lg">
           <div className="space-y-6">
             <div className="grid grid-cols-2 gap-4">
               <div><label className="text-[9px] font-black text-zinc-400 uppercase tracking-widest mb-2 block">Nomenclature</label><input className="w-full border border-zinc-100 bg-zinc-50 rounded-xl p-3.5 font-bold focus:bg-white outline-none" value={newP.title} onChange={e => setNewP({...newP, title: e.target.value})} placeholder="e.g. Barracks Renov" /></div>
               <div><label className="text-[9px] font-black text-zinc-400 uppercase tracking-widest mb-2 block">Client</label><input className="w-full border border-zinc-100 bg-zinc-50 rounded-xl p-3.5 font-bold focus:bg-white outline-none" value={newP.client} onChange={e => setNewP({...newP, client: e.target.value})} /></div>
             </div>
             <div className="grid grid-cols-2 gap-4">
                 <div><label className="text-[9px] font-black text-zinc-400 uppercase tracking-widest mb-2 block">Amount ($)</label><input type="number" className="w-full border border-zinc-100 bg-zinc-50 rounded-xl p-3.5 font-bold focus:bg-white outline-none" value={newP.contractAmount} onChange={e => setNewP({...newP, contractAmount: +e.target.value})} /></div>
                 <div><label className="text-[9px] font-black text-zinc-400 uppercase tracking-widest mb-2 block">Location</label><input className="w-full border border-zinc-100 bg-zinc-50 rounded-xl p-3.5 font-bold focus:bg-white outline-none" value={newP.location} onChange={e => setNewP({...newP, location: e.target.value})} /></div>
             </div>
             <button onClick={saveProject} className="w-full bg-black text-white py-4 rounded-xl font-black text-[10px] uppercase tracking-widest hover:scale-[1.01] transition-all">Confirm Registry</button>
           </div>
       </Modal>
    </div>
  );
};
